import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import {
  getSchoolChangeProposalsAssignedSchoolsQueryKey,
  getV1MeRetrieveQueryOptions,
  schoolChangeProposalsAssignedSchools,
} from '@/api/client'
import type { TeacherAssignedSchool } from '@/api/model'
import { useFetchAllPages } from '@/lib/fetch-all-pages'
import { PageHeader } from '@/components/page-header'
import { SchoolProposalForm } from '@/components/schools/school-proposal-form'
import { Button } from '@/components/ui/button'

export const Route = createFileRoute('/_panel/moje-szkoly/$szkolaId')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    if (me.status === 200 && me.data.role !== 'teacher') {
      throw redirect({ to: '/' })
    }
  },
  component: MojaSzkolaPage,
})

function MojaSzkolaPage() {
  const { szkolaId } = Route.useParams()
  const id = Number(szkolaId)
  const navigate = useNavigate()
  // The teacher's assigned schools carry the proposable fields (FR-008); pick the
  // one being proposed from that list — teachers have no access to /schools/.
  const query = useFetchAllPages<TeacherAssignedSchool>(
    getSchoolChangeProposalsAssignedSchoolsQueryKey(),
    (page, pageSize) => schoolChangeProposalsAssignedSchools({ page, page_size: pageSize }),
  )
  const school = query.items.find((s) => s.id === id)

  if (query.isLoading) {
    return (
      <>
        <PageHeader title="Zgłoś zmianę danych szkoły" />
        <p className="p-6 text-sm text-muted-foreground">Ładowanie…</p>
      </>
    )
  }

  if (!school) {
    return (
      <>
        <PageHeader title="Zgłoś zmianę danych szkoły" />
        <div className="flex flex-col gap-4 p-6">
          <p className="text-sm text-muted-foreground">
            Nie znaleziono szkoły lub nie jesteś do niej przypisany.
          </p>
          <Button variant="outline" onClick={() => navigate({ to: '/moje-szkoly' })}>
            Wróć
          </Button>
        </div>
      </>
    )
  }

  return (
    <>
      <PageHeader title={`Zgłoś zmianę — ${school.name}`} />
      <div className="p-6">
        <p className="mb-4 max-w-3xl text-sm text-muted-foreground">
          Zaproponuj poprawki danych kontaktowych szkoły. Zmiana wejdzie w życie dopiero po
          zatwierdzeniu przez administratora — do tego czasu obowiązują obecne wartości.
        </p>
        <SchoolProposalForm school={school} />
      </div>
    </>
  )
}
